/*************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 *  Copyright 2020 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 **************************************************************************/

// contains implementation to track events as described in the analytics handbook
// https://docs.google.com/spreadsheets/d/1wUBuQiQywJHUO9dfee491pQ0Y_pKHSk4z3DaNoJmM7c/edit?ts=59f0a8a7#gid=430789310
/* global PREVIEW_VERSION */
import { sendEvent, setDocumentId as setDocId, resetDocumentId as resetDocId } from "./interface";
import { lhpFeatures, pageViewModifiers } from "constants/enums";
import {
  getAJSLoadTime,
  getViewerInitTime,
  getFirstPageLoadTime,
  getAJSResourceFetchTimings,
  getDropinQuickToolEnabledTime,
  getDropinQuickToolLoadedTime
} from "utils/performance";
import docApi from "api";
import * as stores from "stores";
import { getIsHUDDocked } from "selectors/hudSelectors";
import { getPageViewModifier } from "selectors/zoom";
import { getBrowser } from "dc-preview-utils/src/Utils/UserAgentUtils";
import { setDocIdForLoggingApi, resetDocIdForLoggingApi } from "logging/logToServer";
import { getFileSizeBytes, lhpFeatureToString, viewModifierToString } from "utils/Telemetry";
import { debounce } from "utils/LiquidModeUtils";
import { isFullScreenMode, getDocumentType } from "selectors/documentSelectors";
import { PAGE_LOAD_EVENTS } from "constants/enums";
import { getViewerAnalyticsForKey } from "../utils/DocumentUtils";
import { analyticsEventTypes } from "../constants/enums";

export * from "./digSigEvents";

const getWorkflow = () => (isFullScreenMode(stores) ? "Full Screen View" : "Classic View");

let defaultPageLoadAnalyticsEventData = {
  [PAGE_LOAD_EVENTS.DOCUMENT_TYPE]: "",
  [PAGE_LOAD_EVENTS.CREATION_DATE]: "",
  [PAGE_LOAD_EVENTS.CREATOR]: "",
  [PAGE_LOAD_EVENTS.DOCUMENT_SIZE]: "",
  [PAGE_LOAD_EVENTS.IS_MODIFIABLE]: false,
  [PAGE_LOAD_EVENTS.HAS_BOOKMARKS]: false,
  [PAGE_LOAD_EVENTS.HAS_COMMENTS]: false,
  [PAGE_LOAD_EVENTS.IS_TAGGED]: false,
  [PAGE_LOAD_EVENTS.PAGE_COUNT]: "",
  [PAGE_LOAD_EVENTS.SECURITY_TYPE]: "",
  [PAGE_LOAD_EVENTS.FILE_SIZE]: "",
  [PAGE_LOAD_EVENTS.IS_SCANNED_DOCUMENT]: false
};

let defaultPageLoadLoggingEventData = {
  [PAGE_LOAD_EVENTS.DOC_LEVEL_JS_TRIGGER_POINTS]: "",
  [PAGE_LOAD_EVENTS.HAS_CALCULATE_JS]: false,
  [PAGE_LOAD_EVENTS.HAS_DOC_JS]: false,
  [PAGE_LOAD_EVENTS.HAS_FIELD_JS]: false,
  [PAGE_LOAD_EVENTS.HAS_FORMAT_JS]: false,
  [PAGE_LOAD_EVENTS.HAS_NEED_APPEARANCE]: false,
  [PAGE_LOAD_EVENTS.HAS_PAGE_JS]: false,
  [PAGE_LOAD_EVENTS.HAS_VALIDATE_JS]: false,
  [PAGE_LOAD_EVENTS.HAS_NON_JS_ACTION]: false,
  [PAGE_LOAD_EVENTS.HAS_MULTIPLE_NON_JS_ACTION]: false,
  [PAGE_LOAD_EVENTS.HAS_UNEMBEDDED_FONTS]: false,
  [PAGE_LOAD_EVENTS.FONT_MATCHING_INFO]: "",
  [PAGE_LOAD_EVENTS.UNEMBEDDED_FONT_NAME]: false,
  [PAGE_LOAD_EVENTS.HAS_ATTACHMENTS]: false,
  [PAGE_LOAD_EVENTS.HAS_DIG_SIG]: false,
  [PAGE_LOAD_EVENTS.HAS_BASE_URL]: false,
  [PAGE_LOAD_EVENTS.HAS_VALID_LIN_DICT]: false,
  [PAGE_LOAD_EVENTS.IS_LINEARIZED_WORKFLOW]: false,
  [PAGE_LOAD_EVENTS.LIN_DICT_E_KEY_VALUE]: "",
  [PAGE_LOAD_EVENTS.NAMED_DESTINATION_COUNT]: "",
  [PAGE_LOAD_EVENTS.IS_DARK_PAGE_THEME_ENABLED]: false,
  [PAGE_LOAD_EVENTS.OVERPRINT_TYPE]: "",
  [PAGE_LOAD_EVENTS.PDF_TITLE]: false
};

let pageLoadAnalyticsData = defaultPageLoadAnalyticsEventData;
let pageLoadLoggingData = defaultPageLoadLoggingEventData;
let fileSizePromise = null;
let hasCommentsDataPromise = null;
let isScannedDocumentPromise = null;

let caretVisibleEventSend = false;

export const getPageLoadAnalyticsData = () => pageLoadAnalyticsData;

export const getPageLoadLoggingData = () => pageLoadLoggingData;

export const resetPageLoadEventData = () => {
  pageLoadAnalyticsData = defaultPageLoadAnalyticsEventData;
  pageLoadLoggingData = defaultPageLoadLoggingEventData;
  fileSizePromise = null;
  hasCommentsDataPromise = null;
  isScannedDocumentPromise = null;
};

export const resetFlagsForOneTimeEvents = () => {
  caretVisibleEventSend = false;
};

export const sendPageLoadEventData = () => {
  Promise.allSettled([fileSizePromise, hasCommentsDataPromise, isScannedDocumentPromise]).then(
    ([fileSizeResult, hasCommentsResult, isScannedDocumentResult]) => {
      if (fileSizeResult.status === "fulfilled") {
        const fileSize = fileSizeResult.value / 1024;
        pageLoadAnalyticsData[PAGE_LOAD_EVENTS.FILE_SIZE] = fileSize;
      }

      if (hasCommentsResult.status === "fulfilled") {
        pageLoadAnalyticsData[PAGE_LOAD_EVENTS.HAS_COMMENTS] = !!hasCommentsResult.value;
      }

      if (isScannedDocumentResult.status === "fulfilled") {
        pageLoadAnalyticsData[PAGE_LOAD_EVENTS.IS_SCANNED_DOCUMENT] = isScannedDocumentResult.value;
      }
      sendEvent("Document profile", "Document properties", "Page Load Info", pageLoadAnalyticsData);
      sendEvent("Document profile", "Document properties", "Page Load Logging Info", pageLoadLoggingData, null, {
        enableAnalytics: false
      });
      resetPageLoadEventData();
    }
  );
};

export const setDocumentId = (documentId, loggingDocumentId = documentId) => {
  setDocId(documentId);
  setDocIdForLoggingApi(loggingDocumentId);
};

export const resetDocumentId = () => {
  resetDocId();
  resetDocIdForLoggingApi();
};

export const trackAJSMetadataDone = data => {
  sendEvent("Document profile", "Document properties", "AJS Metadata success", {}, data);
};

export const trackHasCommentsdata = () => {
  hasCommentsDataPromise = docApi.getCommentsCount();
};

export const trackSecurityType = securityType => {
  pageLoadAnalyticsData[PAGE_LOAD_EVENTS.SECURITY_TYPE] = securityType;
};

export const trackDocumentType = documentType => {
  pageLoadAnalyticsData[PAGE_LOAD_EVENTS.DOCUMENT_TYPE] = documentType;
};

export const trackHasBookmarksMetadata = data => {
  pageLoadAnalyticsData[PAGE_LOAD_EVENTS.HAS_BOOKMARKS] = data.hasBookmarks;
};

export const trackHasDigSigMetadata = data => {
  pageLoadLoggingData[PAGE_LOAD_EVENTS.HAS_DIG_SIG] = data.hasDigitalSignatures;
};

export const trackHasBaseURLMetadata = data => {
  pageLoadLoggingData[PAGE_LOAD_EVENTS.HAS_BASE_URL] = !!data.baseURL;
};

export const trackHasAttachmentsMetadata = data => {
  pageLoadLoggingData[PAGE_LOAD_EVENTS.HAS_ATTACHMENTS] = data.hasAttachments;
};

export const trackNamedDestinationCountMetadata = data => {
  pageLoadLoggingData[PAGE_LOAD_EVENTS.NAMED_DESTINATION_COUNT] = data.namedDestinationCount;
};

export const trackHasValidLinDictMetadata = data => {
  pageLoadLoggingData[PAGE_LOAD_EVENTS.HAS_VALID_LIN_DICT] = data.hasValidLinDict;
};

export const trackLinDictEKeyValueMetadata = data => {
  pageLoadLoggingData[PAGE_LOAD_EVENTS.LIN_DICT_E_KEY_VALUE] = data.linDictEKeyValue;
};

export const trackIsLinearizedWorkflow = data => {
  pageLoadLoggingData[PAGE_LOAD_EVENTS.IS_LINEARIZED_WORKFLOW] = data.isLinearizedWorkflow;
};

export const trackLanguageMetadata = data => {
  pageLoadLoggingData[PAGE_LOAD_EVENTS.LANGUAGE] = data.language;
};

export const trackPDFTitleMetadata = data => {
  pageLoadLoggingData[PAGE_LOAD_EVENTS.PDF_TITLE] = !!data.pdfTitle;
};

export const trackProducerMetadata = data => {
  const { Producer } = data.docInfo;
  pageLoadAnalyticsData[PAGE_LOAD_EVENTS.PRODUCER] = Producer;
};

export const trackOverprintType = results => {
  pageLoadLoggingData[PAGE_LOAD_EVENTS.OVERPRINT_TYPE] = results;
};

export const trackCreatorMetadata = data => {
  const { Creator } = data.docInfo;
  pageLoadAnalyticsData[PAGE_LOAD_EVENTS.CREATOR] = Creator;
};

export const trackCreationDateMetadata = data => {
  const { CreationDate } = data.docInfo;
  pageLoadAnalyticsData[PAGE_LOAD_EVENTS.CREATION_DATE] = CreationDate;
};

export const trackPermissionsMetadata = permissions => {
  pageLoadAnalyticsData[PAGE_LOAD_EVENTS.IS_MODIFIABLE] = permissions;
};

export const trackIsTagged = data => {
  pageLoadAnalyticsData[PAGE_LOAD_EVENTS.IS_TAGGED] = data;
};

export const trackDocLevelJSTriggerPoints = data => {
  pageLoadLoggingData[PAGE_LOAD_EVENTS.DOC_LEVEL_JS_TRIGGER_POINTS] = data.docLevelJSTriggerPoints;
};

export const trackPageCountMetadata = data => {
  pageLoadAnalyticsData[PAGE_LOAD_EVENTS.PAGE_COUNT] = data.numPages;
};

export const trackDocumentSizeMetaData = data => {
  const length = Math.min(data.pages.length, 5);
  if (length === 1) {
    const pageWidth = (data.pages[0].width / 72).toFixed(2);
    const pageHeight = (data.pages[0].height / 72).toFixed(2);
    pageLoadAnalyticsData[PAGE_LOAD_EVENTS.DOCUMENT_SIZE] = `${pageWidth} x ${pageHeight} in`;
  } else {
    const cropBoxes = [];
    for (let i = 0; i < length; i++) {
      const pageWidth = (data.pages[i].width / 72).toFixed(2);
      const pageHeight = (data.pages[i].height / 72).toFixed(2);
      const cropBox = `${pageWidth} x ${pageHeight} in`;
      cropBoxes.push(cropBox);
    }

    const cropBoxesString = cropBoxes.join(", ");
    pageLoadAnalyticsData[PAGE_LOAD_EVENTS.DOCUMENT_SIZE] = cropBoxesString;
  }
};

export const trackFileSize = metadata => {
  fileSizePromise = getFileSizeBytes(metadata);
};

export const trackIsScannedDocument = () => {
  isScannedDocumentPromise = docApi.getIsScannedDocumentFromAJSProvider();
};

export const trackAJSMetadataFailure = () => {
  sendEvent("Document profile", "Document properties", "AJS Metadata Failure");
};

export const trackPasswordDialogEvents = (passwordAttempts, eventType) => {
  sendEvent("Document profile", "Document properties", `Password Dialog ${eventType}`, {}, passwordAttempts, {
    enableAnalytics: false
  });
};

export const trackEditEnabled = partialEditEnabled => {
  if (!partialEditEnabled) {
    sendEvent("User Properties", "VerbAvailability", "EditPermitted");
  }
};

export const trackDocumentClose = () => {
  sendEvent("User actions", "Document actions", "Document close");
};

export const trackViewerInit = () => {
  sendEvent("User actions", "Document actions", "Document opened");
};

export const trackPrint = (status, triggeredFrom) => {
  sendEvent(
    getWorkflow(),
    "Printing",
    "Print event",
    {
      "adb.event.context.dc.printSupported": status
    },
    {
      triggeredFrom
    }
  );
};

export const trackPDFLoadedInPrintHelperIframe = () => {
  const documentType = getDocumentType(stores);
  sendEvent("Print", "PrintHelper", "PDF Loaded In PrintHelper iframe", {
    "adb.event.context.pdfviewer.document_type": documentType
  });
};

export const trackPDFNotLoadedInPrintHelperIframe = () => {
  sendEvent("Print", "PrintHelper", "PDF not loaded in PrintHelper iframe", {}, null, {
    enableAnalytics: false
  });
};

export const trackPrintHelperIframeLoaded = () => {
  sendEvent("Print", "PrintHelper", "PrintHelper iframe loaded", {}, null, {
    enableAnalytics: false
  });
};

export const trackPrintHelperIframeNotLoaded = () => {
  sendEvent("Print", "PrintHelper", "PrintHelper iframe not loaded", {}, null, {
    enableAnalytics: false
  });
};

export const trackPrintORCancelDialogOpened = () => {
  sendEvent(
    "Print",
    "PRINT_OR_CANCEL_DIALOG Opened",
    "Comments and Markups are not included when shared file is being print.",
    {},
    null,
    {
      enableAnalytics: false
    }
  );
};

export const trackPrintFailed = (isFilePasswordProtected, isPrintingAllowed) => {
  if (isFilePasswordProtected && !isPrintingAllowed) {
    sendEvent("Print", "Restricted and Protected PDF", "Print is restricted for Restricted and Protected PDFs");
  } else if (isFilePasswordProtected && isPrintingAllowed) {
    sendEvent("Print", "Password Protected PDF", "Print is not possible for Password Protected PDFs");
  } else if (!isPrintingAllowed) {
    sendEvent("Print", "RESTRICTED_PDF_DIALOG Opened", "Print is restricted in PDF");
  }
};

export const trackFallbackPrint = triggeredFrom => {
  sendEvent("Print", "Normal Print Not supported", "Fallback Print Triggered", {}, `Triggered from: ${triggeredFrom}`, {
    enableAnalytics: false
  });
};

export const trackContentDocumentNotLoadedInIframe = () => {
  sendEvent("Print", "PrintHelper", "contentDocument(PDF) not loaded in iframe");
};

export const trackZoomIn = () => {
  sendEvent(getWorkflow(), "Zoom", "Zoom In");
};

export const trackZoomOut = () => {
  sendEvent(getWorkflow(), "Zoom", "Zoom out");
};

export const trackZoomReset = () => {
  sendEvent(getWorkflow(), "Zoom", "Zoom reset");
};

export const trackSearchOpened = () => {
  sendEvent(getWorkflow(), "Find", "Search UI opened");
};

export const trackSearchExecuted = () => {
  sendEvent(getWorkflow(), "Find", "Search executed");
};

export const trackSearchNext = () => {
  sendEvent(getWorkflow(), "Find", "Click next");
};

export const trackSearchPrev = () => {
  sendEvent(getWorkflow(), "Find", "Click previous");
};

export const trackSearchTripleDotMenuAppeared = () => {
  sendEvent(getWorkflow(), "Find", "Triple dot menu appeared");
};

export const trackSearchTripleDotMenuClicked = () => {
  sendEvent(getWorkflow(), "Find", "Click triple dot menu");
};

export const trackSearchHideSuggestion = () => {
  sendEvent(getWorkflow(), "Find", "Click Hide Suggestion");
};

export const trackSearchShowSuggestion = () => {
  sendEvent(getWorkflow(), "Find", "Click Show Suggestion");
};

export const trackTextCopy = () => {
  sendEvent("Classic View", "Copy", "Text selection & copy success");
};

export const trackThumbnailClick = () => {
  sendEvent("Classic View", "Thumbnail", "click");
};

export const trackAttachmentClick = () => {
  sendEvent("Classic View", "Attachment", "click");
};

export const trackBookmarkClick = title => {
  sendEvent("Classic View", "Bookmark", "click", {
    "adb.event.context.dc.bookmarkTitle": title
  });
};

export const trackHyperlinkClick = () => {
  sendEvent("Classic View", "Hyperlinks", "Hyperlink click");
};

// workflow, subcategory, type, contextData = {}, value
export const trackCopyTextFromContextMenu = () => {
  sendEvent("Commenting", "ContextualMenu", "ClickCopyText", {
    "adb.event.context.dc.ButtonState": "Copied"
  });
};

export const trackShareOpenClick = () => {
  sendEvent("UnifiedShare", "Discover", "ShareClicked", {
    "adb.event.context.dcweb.clickcontext": "Preview"
  });
};

export const trackShareCloseClick = () => {
  sendEvent("UnifiedShare", "Use", "CloseClicked", {
    "adb.event.context.dcweb.clickcontext": "Preview"
  });
};

export const trackLHPFeatureOpenClose = (lhpFeature, isOpened = true) => {
  sendEvent("Left Hand Panel", lhpFeatureToString(lhpFeature), isOpened ? "Open Panel" : "Close Panel");
};
// utility function to call when lhp feature updates, to track open / close
export const trackOnLHPFeatureUpdate = (oldLHPFeature, newLHPFeature) => {
  if (newLHPFeature === lhpFeatures.NONE && oldLHPFeature !== lhpFeatures.NONE) {
    // we must have closed the current feature
    trackLHPFeatureOpenClose(oldLHPFeature, false);
  }
  if (newLHPFeature !== lhpFeatures.NONE && oldLHPFeature !== newLHPFeature) {
    // we must have opened something
    if (oldLHPFeature !== lhpFeatures.NONE) {
      // we have automatically closed the currently opened feature
      // so log that event also
      trackLHPFeatureOpenClose(oldLHPFeature, false);
    }
    trackLHPFeatureOpenClose(newLHPFeature, true);
  }
};

export const trackPageUp = () => {
  sendEvent("User actions", "Page up", "Page up");
};

export const trackPageDown = () => {
  sendEvent("User actions", "Page down", "Page down");
};

export const trackViewModeSwitch = viewModifier => {
  sendEvent("User actions", "Viewing mode", "viewing mode", {
    "adb.event.context.AcrobatJS.ViewMode": viewModifierToString(viewModifier)
  });
};
// utility function that is called on view modifier update
export const trackOnViewModifierUpdate = (oldPageViewModifier, newPageViewModifier) => {
  if (newPageViewModifier !== pageViewModifiers.NO_MOD && newPageViewModifier !== oldPageViewModifier) {
    // user choose a new viewing mode
    trackViewModeSwitch(newPageViewModifier);
  }
};

// we want to track scroll only once
let isHSTracked = false; // keeps track if horizontal scroll has already been tracked
export const trackHorizontalScroll = scrollAmt => {
  if (!isHSTracked) {
    sendEvent("Classic View", "Scroll", "Use horizontal scroll", {
      "adb.event.context.use.horizontalscroll": scrollAmt
    });
    isHSTracked = true;
  }
};
let isVSTracked = false;
export const trackVerticalScroll = scrollAmt => {
  if (!isVSTracked) {
    sendEvent("Classic View", "Scroll", "Use vertical scroll", {
      "adb.event.context.use.verticalscroll": scrollAmt
    });
    isVSTracked = true;
  }
};
// utility function to track either vertical or horizontal scroll
export const trackScroll = (oldX, oldY, newX, newY) => {
  if (newX !== oldX) {
    trackHorizontalScroll(newX - oldX);
  }
  if (newY !== oldY) {
    trackVerticalScroll(newY - oldY);
  }
};

export const trackAJSLoad = () => {
  sendEvent("Document Performance", "Performance", "Viewer load time", {
    "adb.event.context.AcrobatJS.ViewerLoaded": getAJSLoadTime()
  });
};

export const trackFirstPageLoaded = () => {
  // assume marker has been already set
  let extra = {
    "adb.event.context.AcrobatJS.LoadTimeFirstPage": getFirstPageLoadTime(),
    "adb.event.context.AcrobatJS.LoadTime": getViewerInitTime()
  };
  sendEvent("Document Performance", "Performance", "Acrobat JS Time to load first page", extra);
};

export const trackDocumentLoadFailed = failureType => {
  // TODO - will also need to send a context variable "adb.event.context.AcrobatJS.LoadFailureError"
  // Format of this variable is to be decided
  // see comment in the next function
  sendEvent("Document Performance", "Performance", "Acrobat JS Failed to load document", {
    "adb.event.context.AcrobatJS.LoadFailureError": failureType
  });
};

export const trackPageLoadFailed = msg => {
  // TODO :
  // 1. right now we are just logging the exception message
  // but later msg will be an enum. List of entries in the enum is yet
  // to be decided.
  // 2. last 2 context variables have been removed from the handbook
  // confirm on slack and then remove from code here

  sendEvent("Document Performance", "Performance", "Acrobat JS Failed to load page", {
    "adb.event.context.AcrobatJS.ViewMode": viewModifierToString(getPageViewModifier(stores)),
    "adb.event.context.AcrobatJS.hudDock": getIsHUDDocked(stores) ? "Docking" : "Floating"
  });
};

export const trackHasUnembeddedFonts = () => {
  pageLoadLoggingData[PAGE_LOAD_EVENTS.HAS_UNEMBEDDED_FONTS] = true;
};

export const trackImageClick = () => {
  stores.appStore.setImageClickSend(true);
  sendEvent("User Actions", "Document actions", "ImageClick");
};

export const trackUnembeddedFonts = (fontName, replacement) => {
  let eventStr;
  if (replacement === "ExactFont") eventStr = "Substituted with exact match";
  if (replacement === "SystemDefault") eventStr = "Substituted with different font";
  if (replacement === "FauxFont") eventStr = "Font drawn by T4 font fauxing";

  pageLoadLoggingData[PAGE_LOAD_EVENTS.FONT_MATCHING_INFO] = eventStr;
  pageLoadLoggingData[PAGE_LOAD_EVENTS.UNEMBEDDED_FONT_NAME] = fontName;
};

export const trackJP2KAnalytics = jp2kAnalytics => {
  sendEvent("Document profile", "Document properties", "jp2kDeadCode", {
    ...jp2kAnalytics
  });
};

export const trackDownloadFileClicked = triggeredFrom => {
  sendEvent("Document profile", "Document properties", "Document download", {}, { triggeredFrom });
};

export const trackSubmitFormActionRequested = ({ triggeredFrom, containsRequiredEmptyFields, format, protocol }) => {
  sendEvent("Non JS action", "SubmitFormAction", "Submit form action requested", {
    "adb.event.context.submitform.triggeredFrom": triggeredFrom,
    "adb.event.context.submitform.containsRequiredEmptyFields": containsRequiredEmptyFields,
    "adb.event.context.submitform.format": format,
    "adb.event.context.submitform.protocol": protocol
  });
};

export const trackSubmitFormActionTriggered = triggeredFrom => {
  sendEvent("Non JS action", "SubmitFormAction", "Submit form action triggered", {
    "adb.event.context.submitform.triggeredFrom": triggeredFrom
  });
};

export const trackSubmitFormActionResponse = (response, triggeredFrom) => {
  sendEvent("Non JS action", "SubmitFormAction", "Submit form action response", {
    "adb.event.context.submitform.response": response.success,
    "adb.event.context.submitform.numofcallsToServer": response.numOfCallsToServer,
    "adb.event.context.submitform.triggeredFrom": triggeredFrom
  });
};

export const trackUndoRedoCommand = commandType => {
  sendEvent("User Actions", "Document actions", commandType);
};

export const trackRedirectToFilesDropin = () => {
  sendEvent("Document profile", "Document properties", "Redirect to files-dropin", {
    "adb.event.context.pdfviewer.redirectToFilesDropin": true
  });
};

export const trackIsEligibleUser = feature => {
  sendEvent("GenAI Web", "Eligible User", feature);
};

export const trackDocumenOpenedInNightMode = isDarkPageThemeEnabled => {
  pageLoadLoggingData[PAGE_LOAD_EVENTS.IS_DARK_PAGE_THEME_ENABLED] = isDarkPageThemeEnabled;
};

export const trackNightModeFlyout = () => {
  sendEvent("User actions", "Night mode", "customise reading theme button clicked");
};

export const trackNightModePageThemeSwitch = value => {
  sendEvent("User actions", "Night mode", `dark page theme switch - ${value}`);
};

export const trackNightModeAppThemeChange = value => {
  sendEvent("User actions", "Night mode", `app theme - ${value}`);
};

export const trackRedirectToHomeDropin = () => {
  sendEvent("Document profile", "Document properties", "Redirect to home-dropin", {
    "adb.event.context.pdfviewer.redirectToHomeDropin": true
  });
};

export const trackQuickToolVerb = async verbId => {
  const additionalAnalyticsData = await getViewerAnalyticsForKey(analyticsEventTypes.TOOLS_EVENTS);
  sendEvent(
    "Quick Tools",
    "Verb",
    "Executed",
    additionalAnalyticsData?.contextData,
    { verbId },
    undefined,
    {},
    {},
    additionalAnalyticsData?.contentData
  );
};

export const trackTwoColumnViewMode = isTwoColumnView => {
  sendEvent("Classic View", "Two column view mode", "click", {
    "adb.event.context.twocolumn": isTwoColumnView
  });
};

export const trackCoverPageViewMode = isCoverPageMode => {
  sendEvent("Classic View", "Cover Page view mode", "click", {
    "adb.event.context.covermode": isCoverPageMode
  });
};

export const trackRotateView = rotation => {
  const direction = rotation === 90 ? "Clockwise" : rotation === 270 ? "Anti Clockwise" : undefined;
  if (direction) sendEvent("Rotate", `Rotate View ${direction}`, "click");
};

export const trackRotateViewInFillAndSign = () => {
  sendEvent("Rotate Restricted", "Rotate in Fill And Sign", "click");
};

export const trackSwitchToLiquidMode = () => {
  sendEvent("Liquid Mode", "Performance", "Switch to Liquid Mode");
};

export const trackLiquidModeNetworkProxyReady = networkProxyTechnology => {
  sendEvent("Liquid Mode", "Performance", "Network Proxy Ready", {}, { networkProxyTechnology });
};

export const trackDXBundleLoaded = () => {
  sendEvent("Liquid Mode", "Performance", "DX bundle loaded");
};

export const trackLiquidModeViewGenerationStart = () => {
  sendEvent("Liquid Mode", "Performance", "View Generation - START");
};

export const trackLiquidModeViewGenerationEnd = () => {
  sendEvent("Liquid Mode", "Performance", "View Generation - END");
};

export const trackLiquidModeAJSResourcesFetched = debounce(() => trackResourcesFetched());

export const trackResourcesFetched = () => {
  sendEvent("Liquid Mode", "Performance", "Timestamp of recieving each resource", {}, getAJSResourceFetchTimings());
};

export const trackContextMenuVerb = async (verbId, triggerType) => {
  const additionalAnalyticsData = await getViewerAnalyticsForKey(analyticsEventTypes.TOOLS_EVENTS);
  sendEvent(
    "acrobat",
    `${verbId}`,
    "entry:clicked",
    {
      ...additionalAnalyticsData?.contextData,
      "adb.event.context.dcweb.clickcontext": "ContextMenu_MV"
    },
    undefined,
    false,
    {},
    { location: triggerType },
    additionalAnalyticsData?.contentData
  );
};

export const trackContextMenuVerbShown = verbId => {
  sendEvent("acrobat", `${verbId}`, "entry:shown", { "adb.event.context.dcweb.clickcontext": "ContextMenu_MV" });
};

export const trackHCEnabled = theme => {
  sendEvent("High Contrast Mode", "enabled", "enabledTheme", {}, theme);
};

export const trackContextMenuShown = triggerType => {
  sendEvent("acrobat", `${triggerType}`, "contextMenuShown");
};

export const trackHCDisabled = theme => {
  sendEvent("High Contrast Mode", "disabled", "disabledTheme", {}, theme);
};

export const trackHCThemeChange = (oldTheme, newTheme) => {
  const themeSwitch = {
    oldTheme,
    newTheme
  };
  sendEvent("High Contrast Mode", "theme switched", "theme before and after switching", {}, themeSwitch);
};

export const trackReadAloudEntry = () => {
  sendEvent("Read Aloud", "Entry point", "click");
};

export const trackReadAloudVoiceOptionChange = () => {
  sendEvent("Read Aloud", "Voice option", "switch");
};

export const trackReadAloudVoiceRateChange = () => {
  sendEvent("Read Aloud", "Voice rate", "switch");
};

export const trackReadAloudPlayingDuration = durationInSeconds => {
  sendEvent("Read Aloud", "Duration", "Duration played", {}, { durationInSeconds });
};

export const trackDocOpenedWithCaretMode = () => {
  sendEvent("Caret Mode", "Doc Open", "on");
};

export const trackCaretModeOn = () => {
  sendEvent("Caret Mode", "Caret mode - on", "on");
};

export const trackCaretModeOff = () => {
  sendEvent("Caret Mode", "Caret mode - off", "off");
};

export const trackCaretVisible = () => {
  if (!caretVisibleEventSend) {
    sendEvent("Caret Mode", "Caret Visible", "shown");
    caretVisibleEventSend = true;
  }
};

export const trackDocLevelNonJSActions = hasMultipleActions => {
  pageLoadLoggingData[PAGE_LOAD_EVENTS.HAS_NON_JS_ACTION] = true;
  pageLoadLoggingData[PAGE_LOAD_EVENTS.HAS_MULTIPLE_NON_JS_ACTION] = hasMultipleActions;
};

export const trackTriggeredNonJSAction = (params, isSupportedAction = true) => {
  sendEvent("Document profile", "Non JS action", "Non Js action triggered", {
    "adb.event.context.AcrobatJS.actionType": params.actionType,
    "adb.event.context.AcrobatJS.actionSubType": params.actionSubType,
    "adb.event.context.AcrobatJS.isSupportedAction": isSupportedAction
  });
};

export const trackJSActionsData = (params = {}) => {
  const contextData = {};
  Object.keys(params).forEach(key => {
    contextData[`adb.event.context.AcrobatJS.${key}`] = params[key];
  });
  Object.assign(pageLoadLoggingData, contextData);
};

export const trackJSActionAPI = (apiName, isTriggeredAtDocOpen = undefined) => {
  const contextData = { "adb.event.context.AcrobatJS.api": apiName };
  if (isTriggeredAtDocOpen !== undefined) {
    contextData["adb.event.context.AcrobatJS.isTriggeredAtDocOpen"] = isTriggeredAtDocOpen;
  }
  sendEvent("Document profile", "JS Action", "Js action api called:", contextData);
};

export const trackYouTubeLinkCount = linkCnt => {
  sendEvent("YouTube links", "YouTube links count", "count", {}, linkCnt);
};

export const trackCoachMarkAccepted = cohort => {
  sendEvent("collaboration", "fte", "coachmark-accepted", {
    "adb.event.context.dcweb.cohort": cohort
  });
};

export const trackCoachMarkDismissed = cohort => {
  sendEvent("collaboration", "fte", "coachmark-dismissed", {
    "adb.event.context.dcweb.cohort": cohort
  });
};

export const trackDetectSignAnalytics = signDetectionData => {
  const contextData = {
    "adb.event.context.AcrobatJS.startPage": signDetectionData.startPage,
    "adb.event.context.AcrobatJS.numPages": signDetectionData.numPages,
    "adb.event.context.AcrobatJS.detectAll": signDetectionData.detectAll,
    "adb.event.context.AcrobatJS.isSignFieldDetected": signDetectionData.isSignFieldDetected,
    "adb.event.context.AcrobatJS.signatureDetectionTime": signDetectionData.timeTaken
  };
  sendEvent(
    "Document profile",
    "Document properties",
    "Document has sign fields",
    contextData,
    { signDetectionData },
    { enableAnalytics: false }
  );
};

export const trackEntryInFullScreenMode = fullScreenData => {
  const {
    activeMegaVerb,
    activeQuickToolVerb,
    activeRHPVerb,
    isReadAloudActive,
    viewMode,
    pageViewModifier,
    orientation
  } = fullScreenData;
  const contextData = {
    "adb.event.context.AcrobatJS.activeVerb.megaVerb": activeMegaVerb,
    "adb.event.context.AcrobatJS.activeVerb.quickToolVerb": activeQuickToolVerb,
    "adb.event.context.AcrobatJS.activeVerb.RHPVerb": activeRHPVerb,
    "adb.event.context.AcrobatJS.isReadAloudActive": isReadAloudActive,
    "adb.event.context.AcrobatJS.viewMode": viewMode,
    "adb.event.context.AcrobatJS.pageViewModifier": pageViewModifier,
    "adb.event.context.AcrobatJS.orientation": orientation
  };
  sendEvent("Full Screen View", "Full Screen Entry", "Document entered in full screen view", contextData);
};

export const trackExitFromFullScreenMode = fullScreenExitData => {
  const { reasonOfExit, numPagesVisited, totalPages, timeSpent } = fullScreenExitData;
  const contextData = {
    "adb.event.context.AcrobatJS.reason": reasonOfExit,
    "adb.event.context.AcrobatJS.numPagesVisited": numPagesVisited,
    "adb.event.context.AcrobatJS.totalPages": totalPages,
    "adb.event.context.AcrobatJS.timeSpent": timeSpent
  };
  sendEvent("Full Screen View", "Full Screen Exit", "Document exited from full screen mode", contextData);
};

export const trackEntryInFitOneFullPageMode = () => {
  sendEvent("Document profile", "Fit one full page", "Document entered in fit one full page mode");
};

export const trackViewDocPropertiesDialog = triggerPoint => {
  sendEvent("Document profile", "view document properties", "view document properties dialog opened", {
    "adb.event.context.AcrobatJS.triggerPoint": triggerPoint
  });
};

export const trackViewDocPermissionsToast = () => {
  sendEvent("Document profile", "view document properties", "view document permissions toast opened");
};

export const trackDocPropertiesTabClicked = tabName => {
  sendEvent("Document profile", "view document properties", "document permissions dialog tab clicked", {
    "adb.event.context.AcrobatJS.tab": tabName
  });
};

export const trackKeyboardShortcut = (shortcut, action) => {
  sendEvent("User Actions", "KeyboardShortcut", `${shortcut}-${action}`);
};

export const trackMultidocModificationDisabledClicked = context => {
  sendEvent("Multidoc", "FileModificationDisabled", "clicked", undefined, { context });
};

export const trackTopPanelVisible = verbId => {
  sendEvent("TopPanel", verbId, "Visible");
};

export const trackTopPanelExpandedState = (verbId, isExpanded) => {
  sendEvent("TopPanel", verbId, isExpanded ? "Opened" : "Closed");
};

export const trackTopPanelClicked = verbId => {
  sendEvent("TopPanel", verbId, "Clicked");
};
